<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Store, ChevronRight } from 'lucide-vue-next'
import { Alert, Badge, Button, Card, Spinner } from '@/components/ui'
import SetupFlowShell from '@/components/shared/SetupFlowShell.vue'
import {
  selectLoginProvider,
  selectLoginStore,
  type StoreAuthStatus,
  type StoreProviderOption,
  type StoreSelectionResponse,
} from '@/api/authClient'
import { track } from '@/lib/usageTracker'
import { useAuthStore, type AvailableStore } from '@/stores/authStore'

// --- Interface-design checkpoint ---
// Intent: store-chooser for the acting user. Each card surfaces store
// name, StoreDB / group provenance, and (when known) the connection
// health. No raw "select a card" grid - cards are operationally rich.
// Palette: paper/ink/receipt-green, compact density, no marketing copy.

const router = useRouter()
const store = useAuthStore()

const loading = ref(false)
const apiError = ref(false)
const networkError = ref(false)
const errorMessage = ref('We could not look up that store.')
const pendingStore = ref<AvailableStore | null>(null)

const contextItems = computed(() => [
  {
    label: 'Acting context',
    value: store.user?.email ? `Signed in as ${store.user.email}` : 'No user',
    tone: store.user?.email ? 'success' : 'warning',
  },
  {
    label: 'Stores available',
    value: store.availableStores.length > 0 ? `${store.availableStores.length} store(s)` : 'None',
    tone: store.availableStores.length > 0 ? 'default' : 'warning',
  },
  {
    label: 'Verification state',
    value: loading.value
      ? 'Loading store state'
      : (apiError.value || networkError.value)
        ? 'Action needed'
        : 'Awaiting selection',
    tone: loading.value
      ? 'accent'
      : apiError.value
        ? 'error'
        : (networkError.value ? 'warning' : 'default'),
  },
])

function resetUI() {
  loading.value = false
  apiError.value = false
  networkError.value = false
}

async function selectAndCheckStore(target: AvailableStore) {
  track('btn:OpenStore', 'click', { storeId: target.storeId })
  resetUI()
  if (!store.user) {
    router.replace({ name: 'UserLogin' })
    return
  }
  pendingStore.value = target
  store.selectStore(target)
  try {
    loading.value = true
    const response: StoreSelectionResponse = await selectLoginStore({
      userId: store.user.id,
      storeId: target.storeId,
      storeDb: target.storeDb,
    })
    routeResponse(response)
  } catch (e) {
    const msg = String((e as Error)?.message || e)
    if (/failed to fetch|network|TypeError/i.test(msg)) {
      networkError.value = true
    } else {
      apiError.value = true
      errorMessage.value = msg
    }
    console.error(e)
  } finally {
    loading.value = false
    pendingStore.value = null
  }
}

function routeResponse(response: StoreSelectionResponse) {
  if (!response) return
  const status: StoreAuthStatus = response.status
  if (status === 'READY' && response.provider && response.apiKey) {
    store.selectPackage(response.provider.provider)
    store.setIntegrationResult({
      provider: response.provider.provider,
      apiKey: response.apiKey,
      integrationId: response.integrationId ?? undefined,
      authStatus: 'ready',
    })
    store.setProviderOptions([])
    router.replace({ name: 'Dashboard' })
    return
  }
  if (status === 'PROVIDER_SELECTION_REQUIRED') {
    store.setProviderOptions((response.providers ?? []) as StoreProviderOption[])
    router.replace({ name: 'LoginProviderSelection' })
    return
  }
  if (status === 'AUTH_REQUIRED' && response.provider) {
    store.setProviderAuthRequired(response.provider.provider, response.authRedirectUrl ?? null)
    if (response.authRedirectUrl) {
      // Reuse the existing provider OAuth pathway.
      window.location.href = response.authRedirectUrl
      return
    }
    router.replace({ name: 'LoginProviderSelection' })
    return
  }
  // NO_ACCOUNT or anything else: surface the no-account experience.
  store.setProviderOptions([])
  router.replace({ name: 'InvoiceAccess' })
}

function logout() {
  store.logout()
  router.replace({ name: 'UserLogin' })
}

onMounted(() => {
  if (!store.user || store.availableStores.length === 0) {
    router.replace({ name: 'UserLogin' })
  }
})
</script>

<template>
  <SetupFlowShell
    eyebrow="Choose a store"
    title="Which store do you want to work on?"
    subtitle="Each card carries the store name, group reference, and the database the system will resolve. Pick one to continue."
    :current-step="2"
    :steps="['Sign in', 'Choose store', 'Connect', 'Workspace']"
    :context-items="contextItems"
  >
    <Card padding="lg" class="store-panel">
      <div class="store-panel__actions">
        <Badge variant="muted">{{ store.availableStores.length }} store(s)</Badge>
        <Button variant="ghost" id="logoutButton" @click="() => { track('btn:SignOut', 'click'); logout() }">Sign out</Button>
      </div>

      <div v-if="store.availableStores.length === 0" class="store-panel__empty">
        <Alert id="noStores" variant="warning" title="No stores available">
          This account is not associated with any store. Contact your administrator.
        </Alert>
      </div>

      <div v-else class="store-panel__grid">
        <Card
          v-for="entry in store.availableStores"
          :key="entry.storeId"
          padding="md"
          class="store-card"
          :class="{ 'store-card--pending': pendingStore?.storeId === entry.storeId }"
        >
          <div class="store-card__row">
            <div class="store-card__icon" aria-hidden="true">
              <Store :size="18" />
            </div>
            <div class="store-card__body">
              <div class="store-card__name">{{ entry.storeName || entry.storeId }}</div>
              <div class="store-card__meta">
                <span class="store-card__id">Store ID {{ entry.storeId }}</span>
                <span class="store-card__sep">·</span>
                <span class="store-card__db">{{ entry.storeDb }}</span>
              </div>
              <div class="store-card__provenance">
                <Badge variant="muted">Group {{ entry.group }}</Badge>
              </div>
            </div>
            <Button
              size="sm"
              variant="primary"
              class="store-card__action"
              :disabled="loading"
              @click="selectAndCheckStore(entry)"
            >
              <Spinner v-if="loading && pendingStore?.storeId === entry.storeId" size="sm" />
              <span v-if="!loading || pendingStore?.storeId !== entry.storeId">Open</span>
              <span v-else>Opening</span>
              <ChevronRight v-if="!loading || pendingStore?.storeId !== entry.storeId" :size="14" />
            </Button>
          </div>
        </Card>
      </div>

      <Alert
        v-if="apiError"
        id="storeApiError"
        variant="error"
        title="Store lookup failed"
      >
        {{ errorMessage }}
        <div class="store-panel__alert-actions">
          <Button id="retryStore" @click="() => { track('btn:RetryStores', 'click'); pendingStore && selectAndCheckStore(pendingStore) }">Try again</Button>
        </div>
      </Alert>

      <Alert
        v-else-if="networkError"
        id="storeNetworkError"
        variant="warning"
        title="Network unreachable"
      >
        We couldn't reach the Lyt Accounting service. Check your connection and retry.
        <div class="store-panel__alert-actions">
          <Button id="retryStoreNetwork" @click="() => { track('btn:RetryStores', 'click'); pendingStore && selectAndCheckStore(pendingStore) }">Retry</Button>
        </div>
      </Alert>
    </Card>
  </SetupFlowShell>
</template>

<style scoped>
.store-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.store-panel__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

.store-panel__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.65rem;
}

.store-card {
  cursor: default;
  transition: border-color 0.15s ease;
}

.store-card:hover {
  border-color: var(--color-border-strong);
}

.store-card--pending {
  border-color: var(--color-primary);
}

.store-card__row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.store-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.4rem;
  height: 2.4rem;
  border: 1px solid var(--color-border-soft);
  border-radius: var(--radius-sm);
  background: var(--color-surface-muted);
  color: var(--color-ink);
}

.store-card__body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.store-card__name {
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-ink);
}

.store-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: center;
  font-size: 0.82rem;
  color: var(--color-ink-soft);
}

.store-card__id,
.store-card__db {
  font-variant-numeric: tabular-nums;
}

.store-card__sep {
  color: var(--color-border-strong);
}

.store-card__provenance {
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.store-card__action {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.store-panel__empty {
  display: block;
}

.store-panel__alert-actions {
  margin-top: 0.6rem;
}

@media (min-width: 720px) {
  .store-panel__grid {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
